//===================================
// Moving text with copy
//===================================

import { observeWhenInView } from '../utils';

const MovingTextWithCopy = (ele) => {
  const movingTextEle = ele.querySelector('.moving-text');
  const textImg = movingTextEle.querySelector('img');

  /**
   * Initialize the moving text with copy.
   * 
   * @returns {void}
   */
  const init = () => {
    observeWhenInView(ele, checkInView, {rootMargin: '50% 0% 50% 0%'});
  }

  /**
   * Check if the element is in view.
   * 
   * @param {boolean} inView 
   * @param {function} unobserve 
   * @returns {Promise<void>}
   */
  const checkInView = async (inView, unobserve) => {
    if (!inView) return;

    unobserve();
    await build();
    animate();
  }

  /**
   * Build the html structure for the moving text.
   * 
   * @returns {Promise<void>}
   */
  const build = async () => {
    // load the image
    await new Promise(res => {
      textImg.onload = res;
      textImg.src = textImg.dataset.src;
    });

    // build the text rows
    const row = document.createElement('div');
    const innerRow = document.createElement('div');
    row.classList.add('text-row', 'overflow-hidden');
    innerRow.classList.add('flex', 'gap-x-20');
    row.appendChild(innerRow);
    movingTextEle.appendChild(row);

    // add the text image to the row
    innerRow.appendChild(textImg);

    // clone the text images
    const textImgWidth = textImg.width;
    const rowWidth = movingTextEle.offsetWidth;

    let textImgCount = Math.ceil(rowWidth / textImgWidth) + 2;

    while (textImgCount--) {
      const textImgCopy = textImg.cloneNode(true);
      innerRow.appendChild(textImgCopy);
    }

    // clone the text row
    movingTextEle.appendChild(row.cloneNode(true));
    
    // ensure all images have loaded
    const images = movingTextEle.querySelectorAll('img');

    await Promise.all(
      [...images].map(img => {
        return new Promise(res => {
          img.complete && res();
          img.onload = res;
        });
      })
    );

    return;
  }

  /**
   * Animate the text
   * 
   * @returns {void}
   */
  const animate = () => {
    let _prevWindowScroll = window.scrollY;
    let _scroll = 0;

    const _rows = movingTextEle.querySelectorAll('.text-row');
    const _maxRowScroll = _rows[0].scrollWidth - window.innerWidth;

    /**
     * Check if reset is needed.
     * 
     * @param {object} args
     * @returns {number}
     */
    const checkIfResetNeeded = ({scroll, rows}) => {
      const firstImg = rows[0].querySelector('img:first-child');
      const secondImg = rows[0].querySelector('img:nth-child(2)');

      if (firstImg.getBoundingClientRect().left > 0) {
        return secondImg.getBoundingClientRect().left;
      }

      if (secondImg.getBoundingClientRect().left < 0) {
        return 0;
      }

      return scroll;
    }

    /**
     * Adjust the scroll speed based scroll acceleration.
     * 
     * @param {object} args
     * @returns {number}
     */
    const accelerationAdjustment = ({scroll, prevWindowScroll}) => {
      return scroll += (window.scrollY - prevWindowScroll) * 0.4 + 1;
    }

    /**
     * Set the transform of the rows.
     * 
     * @param {object} args
     * @returns {void}
     */
    const setTransform = ({scroll, rows, maxRowScroll}) => {
      const evenScroll = -scroll;
      const oddScroll = scroll - maxRowScroll;

      rows.forEach((row, i) => {
        const innerRow = row.querySelector('div');
        innerRow.style.transform = `translateX(${i % 2 ? oddScroll : evenScroll}px)`;
      });
    }

    /**
     * Animation loop
     * 
     * @returns {void}
     */
    const play = () => {
      let scroll = _scroll;

      const args = {
        rows: _rows,
        maxRowScroll: _maxRowScroll,
        prevWindowScroll: _prevWindowScroll
      }

      scroll = checkIfResetNeeded({scroll, ...args});
      scroll = accelerationAdjustment({scroll, ...args});
      setTransform({scroll, ...args});

      _scroll = scroll;
      _prevWindowScroll = window.scrollY;
      
      requestAnimationFrame(play);
    }

    play();
  }

  init();
}

document.addEventListener('DOMContentLoaded', () => {
  const eles = document.querySelectorAll('.moving-text-with-copy');

  eles.forEach(MovingTextWithCopy);
});
